/**
 * Registry of flow saves that are still on the wire.
 *
 * Navigating home from the editor no longer awaits its forced save — that wait
 * made the logo feel dead for the length of an auth round-trip plus a
 * full-graph upsert. The cost of not waiting is that a save can still be in
 * flight after the editor is gone, and three things have to respect that:
 *
 *   - the browser, because closing the tab mid-save loses the write. The
 *     editor's own beforeunload guard is torn down by the very navigation that
 *     backgrounds the save, so the guard lives here instead, at module scope.
 *   - the dashboard listing, because reading the row before its save lands
 *     shows a stale name/thumbnail and then freezes that stale copy into the
 *     cross-mount snapshot.
 *   - dashboard mutations, because a rename, archive or delete issued while
 *     the upsert is still travelling would be silently reverted by it — or, in
 *     the delete case, resurrect the knod.
 */

import logger from '../utils/logger';

type Pending = Promise<unknown>;

const inFlight = new Map<string, Set<Pending>>();

/**
 * Register an in-flight save for `flowId`. Returns the promise unchanged, so
 * the caller still owns its result and its errors.
 */
export function trackFlowSave<T>(flowId: string, promise: Promise<T>): Promise<T> {
  let bucket = inFlight.get(flowId);
  if (!bucket) {
    bucket = new Set();
    inFlight.set(flowId, bucket);
  }
  bucket.add(promise);

  // Unregister on settle, success or failure. The `.catch` is only here so
  // this bookkeeping chain never becomes an unhandled rejection of its own.
  void promise
    .catch(() => undefined)
    .finally(() => {
      const current = inFlight.get(flowId);
      if (!current) return;
      current.delete(promise);
      if (current.size === 0) inFlight.delete(flowId);
    });

  return promise;
}

export function hasPendingFlowSaves(flowId?: string): boolean {
  if (flowId !== undefined) return (inFlight.get(flowId)?.size ?? 0) > 0;
  return inFlight.size > 0;
}

/** Long enough for a slow upsert with an R2 upload behind it. */
export const MUTATION_SETTLE_TIMEOUT_MS = 15_000;
/** Reads only risk showing a stale row, so they give up much sooner. */
export const READ_SETTLE_TIMEOUT_MS = 3_000;

/**
 * Resolve once no save is in flight — for `flowId` if given, otherwise for any
 * flow. A failed save counts as settled: this orders operations, it does not
 * guarantee they succeeded.
 *
 * Always bounded. A save carrying a large graph or an R2 upload can take a
 * long time and, on a dead connection, effectively never finish; waiting
 * without a ceiling would let one stuck request freeze the dashboard outright.
 * Timing out is the lesser failure — a stale read, or a mutation that races
 * the save — and it is logged so the cause is visible.
 *
 * Loops because a save can be queued while we await an earlier one.
 */
export async function settleFlowSaves(
  flowId?: string,
  timeoutMs: number = MUTATION_SETTLE_TIMEOUT_MS
): Promise<void> {
  const deadline = Date.now() + timeoutMs;

  while (hasPendingFlowSaves(flowId)) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      logger.warn(
        `[pendingFlowSaves] Gave up waiting after ${timeoutMs}ms for` +
        `${flowId ? ` flow ${flowId}` : ' pending saves'} — proceeding anyway`
      );
      return;
    }

    const pending =
      flowId !== undefined
        ? [...(inFlight.get(flowId) ?? [])]
        : [...inFlight.values()].flatMap((bucket) => [...bucket]);
    if (pending.length === 0) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const expiry = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, remaining);
    });
    try {
      await Promise.race([Promise.allSettled(pending), expiry]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }
}

// ── Deletion tombstones ────────────────────────────────────────────
//
// settleFlowSaves is bounded, and the wait it bounds includes the blob->R2
// upload that runs *before* the upsert is issued. On a knod carrying fresh
// video that upload can outlast the ceiling, so a delete would proceed while
// the save has not even reached the database — and the upsert, being an
// upsert, would then recreate the row. Not a race: a certainty.
//
// A tombstone closes it from the other side. deleteFlow records the id, and
// the save re-checks it immediately before writing and abandons instead.
const deleted = new Set<string>();

export function markFlowDeleted(flowId: string): void {
  deleted.add(flowId);
}

/** Undo the tombstone when a delete did not actually take effect. */
export function unmarkFlowDeleted(flowId: string): void {
  deleted.delete(flowId);
}

export function isFlowDeleted(flowId: string): boolean {
  return deleted.has(flowId);
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (inFlight.size === 0) return;
    e.preventDefault();
    e.returnValue = '';
  });
}
